import { Injectable, inject } from '@angular/core';
import { first, map, mergeMap, of, tap, zip } from 'rxjs';
import { EffectsBase } from 'clicklease-ui-elements';
import { LeaseService, PromisesService } from '../../services';
import {
    SetContractSelectedIdAction,
    getContractSelectedIdSelector,
    ResetPaymentsAction,
    ResetContractSelectedIdAction,
    SetPaymentsAction,
    LoadPaymentsAction,
} from '../../ngrx';
import { ONE_TIME_PAYMENT_STATUS, PaymentData, PromiseToPayFilterRequestConditions } from '../../interfaces';

@Injectable()
export class PaymentsEffectsService extends EffectsBase {
    readonly #leaseService = inject(LeaseService);
    readonly #promisesService = inject(PromisesService);
    readonly #contractSelected = this.store.selectSignal(
        getContractSelectedIdSelector
    );

    public readonly getPaymentList = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetContractSelectedIdAction),
            tap(() => this.store.dispatch(LoadPaymentsAction())),
            mergeMap(() => this.loadPaymentList())
        )
    );

    public readonly resetPaymentList = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(ResetContractSelectedIdAction),
            mergeMap(() => of(ResetPaymentsAction()))
        )
    );

    private loadPaymentList() {
        const contract = this.#contractSelected();
        const conditions: PromiseToPayFilterRequestConditions = {field: 'applicationId', operator: 'equals', value: contract};
        return zip(this.#leaseService.getPaymentList(contract), this.#promisesService.getPromiseList(conditions))
            .pipe(
                map(([plan, promises]) => {
                    const payments: PaymentData[] = [
                        ...(plan?.payments ?? []), 
                        ...(promises?.promisePayments ?? []).filter(b => b.paymentStatus !== ONE_TIME_PAYMENT_STATUS.CANCELLED).map(p => ({
                            id: this.#asText(p.promiseId),
                            promiseId: p.promiseId,
                            date: p.paymentDate,
                            paymentMethod: p.paymentMethod,
                            paymentAmount: p.amount,
                            paymentReference: this.#asText(p.externalPaymentId),
                            applicationId: p.applicationId,
                            paymentStatus: p.paymentStatus,
                            satisfied: p.paymentStatus === ONE_TIME_PAYMENT_STATUS.KEPT,
                            reversed: p.paymentStatus === ONE_TIME_PAYMENT_STATUS.BROKEN,
                            reversedReason: p.terminationReason,
                            bankAccount: {
                                id: this.#asText(p.bankAccountId),
                                last4Number: p.bankAccountLastFourDigits,
                                bankName: p.bankName
                            }
                        }))
                    ];
                    payments.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                    return SetPaymentsAction({ list: { list: payments }, next: plan?.nextPayment });
                }),
                first()
            );
    }

    /** The API can return null/undefined for ids that are typed as required, so never call toString() directly on them. */
    #asText(value: number | string | null | undefined): string {
        return value === null || value === undefined ? '' : value.toString();
    }
}
